import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { usePermissions, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    Claims,
    DefaultPagedResponse,
    Entity,
    AccountType,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
} from "../models";
import { Operation } from "rfc6902";

export const useAccountTypeQuery = (params?: QueryHookParams): IUseQueryPaged<AccountType> => {
    const accountTypeId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { hasPermission } = usePermissions();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.AccountTypes);
    const url = Endpoints.AccountTypes;
    const queryType = getQueryType(accountTypeId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<AccountType>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.accountTypes.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: hasPermission(Claims.ViewAccount) && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<AccountType>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.accountTypes.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: hasPermission(Claims.ViewAccount) && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<AccountType>({ url: `${url}/${accountTypeId}`, config }),
        queryKey: queryKeys.accountTypes.detail(accountTypeId),
        placeholderData: undefined,
        enabled: hasPermission(Claims.ViewAccount) && queryType === QueryType.Detail && !!params?.id,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<AccountType>) => post<Partial<AccountType>, AccountType>({ url, data }),
        queryKey: queryKeys.accountTypes.all(),
        keysToInvalidate: [queryKeys.accountTypes.lists(), queryKeys.accountTypes.pagedLists()],
        successToast: "Account type created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], AccountType>({ url: `${url}/${accountTypeId}`, data }),
        queryKey: queryKeys.accountTypes.detail(accountTypeId),
        keysToInvalidate: [queryKeys.accountTypes.lists(), queryKeys.accountTypes.pagedLists()],
        successToast: "Account type updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${accountTypeId}` }),
        queryKey: queryKeys.accountTypes.detail(accountTypeId),
        keysToInvalidate: [queryKeys.accountTypes.lists(), queryKeys.accountTypes.pagedLists()],
        successToast: "Account type deleted",
    });

    return useMemo<IUseQueryPaged<AccountType>>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            updateMutation,
            deleteMutation,
        }),
        [pagedListQuery, listQuery, detailQuery, createMutation, updateMutation, deleteMutation]
    );
};
